import { runInAction } from 'mobx';
import { MobXProviderContext } from 'mobx-react';
import React from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { ConversationMessageReactionDto } from '../../index.types';

/**
 * Custom hook to update message in MobX stores
 * Updates:
 * - MobX groupedMessagesByConversationMap
 * - Pinned messages if applicable
 *
 * @returns A callback function to update message in MobX stores
 */
export const useUpdateMessageCache = () => {
  const rootStore = React.useContext<RootStoreProps>(MobXProviderContext);

  const updateMessageCache = React.useCallback(
    (updatedMessage: ConversationMessageReactionDto) => {
      // 1. Update MobX message store
      const messageGroup =
        rootStore.message.groupedMessagesByConversationMap.get(
          updatedMessage.conversationId
        );

      if (messageGroup) {
        const message = messageGroup.findMessageById(updatedMessage.id);

        if (message) {
          message.setReactions(updatedMessage.reactions || {});
        }
      }

      // 2. Update pinned messages if this message is pinned
      const pinnedMessages = rootStore.ui.listOfPinnedMessages.get(
        updatedMessage.conversationId
      );

      if (pinnedMessages) {
        const pinnedMessageIndex = pinnedMessages.findIndex(
          (pm) => pm.id === updatedMessage.id
        );

        if (pinnedMessageIndex !== -1) {
          const updatedPinnedMessages = [...pinnedMessages];
          updatedPinnedMessages[pinnedMessageIndex] = {
            ...updatedPinnedMessages[pinnedMessageIndex],
            reactions: updatedMessage.reactions,
          };

          runInAction(() => {
            rootStore.ui.listOfPinnedMessages.set(
              updatedMessage.conversationId,
              updatedPinnedMessages
            );
          });
        }
      }
    },
    [rootStore]
  );

  return updateMessageCache;
};
